/**
 * MetricCard Component - プロフェッショナルデザインシステム
 * 競馬予測システム専用のメトリクス表示コンポーネント
 * アクセシビリティ・視覚階層・情報設計完全対応
 */

import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card } from './Card';
import { Heading, Text } from './Typography';
import { HStack, Stack } from '../layout/Layout';
import { tokens, semanticTokens, utils } from '../tokens';

const MetricCard = forwardRef(({
  title,
  value,
  unit = '',
  target,
  change,
  changeLabel,
  status = 'neutral', // 'success' | 'warning' | 'error' | 'neutral'
  icon: Icon,
  description,
  loading = false,
  onClick,
  className = '',
  ...props
}, ref) => {
  
  // ステータス別のカラーテーマ
  const statusThemes = {
    success: {
      primary: tokens.colors.success[500],
      secondary: tokens.colors.success[100],
      text: tokens.colors.success[700],
      border: tokens.colors.success[200],
    },
    warning: {
      primary: tokens.colors.warning[500],
      secondary: tokens.colors.warning[100],
      text: tokens.colors.warning[700],
      border: tokens.colors.warning[200],
    },
    error: {
      primary: tokens.colors.error[500],
      secondary: tokens.colors.error[100],
      text: tokens.colors.error[700],
      border: tokens.colors.error[200],
    },
    neutral: {
      primary: tokens.colors.primary[500],
      secondary: tokens.colors.primary[50],
      text: tokens.colors.primary[700],
      border: tokens.colors.primary[200],
    },
  };
  
  const theme = statusThemes[status];
  
  // 変化の方向を判定
  const changeDirection = change > 0 ? 'up' : change < 0 ? 'down' : 'neutral';
  const ChangeIcon = changeDirection === 'up' ? TrendingUp : 
                    changeDirection === 'down' ? TrendingDown : Minus;
  
  // パフォーマンスの可視化（プログレスバー）
  const progressPercentage = target ? Math.min((value / target) * 100, 100) : null;
  
  return (
    <Card
      ref={ref}
      interactive={!!onClick}
      onClick={onClick}
      padding="medium"
      elevation={2}
      className={className}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? `${title}: ${value}${unit}` : undefined}
      {...props}
    >
      <Stack spacing={4}>
        {/* ヘッダー部分 */}
        <HStack justify="space-between" align="start">
          <Stack spacing={1}>
            <Text 
              size="sm" 
              color="secondary" 
              weight="medium"
              id={`metric-${title.replace(/\s+/g, '-').toLowerCase()}-label`}
            >
              {title}
            </Text>
            {description && (
              <Text 
                size="xs" 
                color="tertiary"
                id={`metric-${title.replace(/\s+/g, '-').toLowerCase()}-desc`}
              >
                {description}
              </Text>
            )}
          </Stack>
          
          {Icon && (
            <div
              style={{
                padding: tokens.spacing[2],
                borderRadius: tokens.borders.radius.lg,
                backgroundColor: theme.secondary,
                color: theme.primary,
              }}
              aria-hidden="true"
            >
              <Icon size="1.25rem" />
            </div>
          )}
        </HStack>
        
        {/* メイン値表示 */}
        <div>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[2] }}>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                style={{
                  width: '1rem',
                  height: '1rem',
                  border: `2px solid ${tokens.colors.gray[200]}`,
                  borderTop: `2px solid ${theme.primary}`,
                  borderRadius: '50%',
                }}
              />
              <Text color="tertiary">読み込み中...</Text>
            </div>
          ) : (
            <HStack align="baseline" spacing={1}>
              <Heading 
                level={2} 
                size={tokens.typography.fontSize['3xl']}
                color="primary"
                aria-labelledby={`metric-${title.replace(/\s+/g, '-').toLowerCase()}-label`}
                aria-describedby={`metric-${title.replace(/\s+/g, '-').toLowerCase()}-desc`}
              >
                {typeof value === 'number' 
                  ? value.toLocaleString('ja-JP')
                  : value
                }
              </Heading>
              {unit && (
                <Text size="lg" color="secondary" weight="medium">
                  {unit}
                </Text>
              )}
            </HStack>
          )}
        </div>
        
        {/* 変化・目標表示 */}
        {(change !== undefined || target !== undefined) && (
          <Stack spacing={2}>
            {/* 変化表示 */}
            {change !== undefined && (
              <HStack align="center" spacing={1}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
                    borderRadius: tokens.borders.radius.base,
                    backgroundColor: changeDirection === 'up' ? tokens.colors.success[100] :
                                   changeDirection === 'down' ? tokens.colors.error[100] :
                                   tokens.colors.gray[100],
                    color: changeDirection === 'up' ? tokens.colors.success[700] :
                          changeDirection === 'down' ? tokens.colors.error[700] :
                          tokens.colors.gray[700],
                  }}
                  role="img"
                  aria-label={`変化: ${change > 0 ? '増加' : change < 0 ? '減少' : '変化なし'}`}
                >
                  <ChangeIcon size="0.875rem" style={{ marginRight: tokens.spacing[1] }} />
                  <Text size="sm" weight="medium">
                    {Math.abs(change).toFixed(2)}%
                  </Text>
                </div>
                {changeLabel && (
                  <Text size="sm" color="tertiary">
                    {changeLabel}
                  </Text>
                )}
              </HStack>
            )}
            
            {/* 目標達成度プログレスバー */}
            {target && progressPercentage !== null && (
              <div>
                <HStack justify="space-between" align="center" spacing={0}>
                  <Text size="xs" color="tertiary">
                    目標: {typeof target === 'number' ? target.toLocaleString('ja-JP') : target}{unit}
                  </Text>
                  <Text size="xs" color="tertiary" weight="medium">
                    {progressPercentage.toFixed(0)}%
                  </Text>
                </HStack>
                
                <div
                  style={{
                    width: '100%',
                    height: '4px',
                    backgroundColor: tokens.colors.gray[200],
                    borderRadius: tokens.borders.radius.full,
                    overflow: 'hidden',
                    marginTop: tokens.spacing[1],
                  }}
                  role="progressbar"
                  aria-valuenow={progressPercentage}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`目標達成度: ${progressPercentage.toFixed(0)}%`}
                >
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercentage}%` }}
                    transition={{ duration: 1, ease: 'easeOut' }}
                    style={{
                      height: '100%',
                      backgroundColor: progressPercentage >= 100 ? tokens.colors.success[500] :
                                     progressPercentage >= 80 ? theme.primary :
                                     progressPercentage >= 60 ? tokens.colors.warning[500] :
                                     tokens.colors.error[500],
                      borderRadius: tokens.borders.radius.full,
                    }}
                  />
                </div>
              </div>
            )}
          </Stack>
        )}
      </Stack>
      
      {/* ホバー時のインタラクティブエフェクト */}
      {onClick && (
        <motion.div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: tokens.borders.radius.lg,
            background: `linear-gradient(45deg, transparent 30%, ${utils.alpha(theme.primary, 0.05)} 70%)`,
            opacity: 0,
            pointerEvents: 'none',
          }}
          whileHover={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        />
      )}
    </Card>
  );
});

MetricCard.displayName = 'MetricCard';

// 特化型メトリクスカード：還元率表示用
const ReturnRateCard = forwardRef(({
  returnRate,
  target = 0.8,
  previousRate,
  className = '',
  ...props
}, ref) => {
  const change = previousRate ? ((returnRate - previousRate) / previousRate) * 100 : undefined;
  const status = returnRate >= 0.95 ? 'success' :
                returnRate >= 0.85 ? 'neutral' :
                returnRate >= 0.75 ? 'warning' : 'error';
  
  return (
    <MetricCard
      ref={ref}
      title="還元率"
      value={(returnRate * 100).toFixed(2)}
      unit="%"
      target={(target * 100).toFixed(0)}
      change={change}
      changeLabel="前回比"
      status={status}
      description={`目標: ${(target * 100).toFixed(0)}%以上`}
      className={className}
      {...props}
    />
  );
});

ReturnRateCard.displayName = 'ReturnRateCard';

// 特化型メトリクスカード：的中率表示用
const HitRateCard = forwardRef(({
  hitRate,
  target = 0.15,
  previousRate,
  className = '',
  ...props
}, ref) => {
  const change = previousRate ? ((hitRate - previousRate) / previousRate) * 100 : undefined;
  const status = hitRate >= target ? 'success' : 'neutral';
  
  return (
    <MetricCard
      ref={ref}
      title="的中率"
      value={(hitRate * 100).toFixed(2)}
      unit="%"
      target={(target * 100).toFixed(0)}
      change={change}
      changeLabel="前回比"
      status={status}
      description="単勝レース的中率"
      className={className}
      {...props}
    />
  );
});

HitRateCard.displayName = 'HitRateCard';

export { MetricCard, ReturnRateCard, HitRateCard };